import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { DynamicListsApi } from './api'
import { RuleEditor, type ListOption, type ListsMeta } from './RuleEditor'
import { newRule, ruleProblem, templateText } from './rule'
import { usePreview } from './hooks'
import { FeatherIcon } from '../components/FeatherIcon'
import { Modal } from './shared'
import type { Catalog, DynamicList, FilterType, Preview, Rule, Schedule, Template } from './types'
import { LoadingBlock, BusyLabel, Spinner } from '../components/Spinner'

export function PreviewPanel({ preview, loading, error, problem }: { preview: Preview | null; loading: boolean; error: string; problem: string }) {
  const { t } = useTranslation('dynamicLists')
  const noFacts = preview && preview.total === 0 && !preview.facts.last_pass_finished_at
  return (
    <section className="dl-preview" aria-live="polite" aria-label={t('preview.title')}>
      <header className="dl-preview-head">
        <h3 className="dl-h3">{t('preview.title')}</h3>
        {loading && <Spinner size={14} label={t('preview.updating')} />}
      </header>
      {problem ? (
        <p className="dl-warn">{problem}</p>
      ) : error ? (
        <p className="dl-error">{error}</p>
      ) : noFacts ? (
        <p className="dl-muted">{t('preview.noFacts')}</p>
      ) : preview ? (
        <>
          <p className="dl-preview-big">
            <strong>{t('preview.matched', { count: preview.matched })}</strong>{' '}
            <span className="dl-muted">{t('preview.of', { count: preview.total })}</span>
          </p>
          {preview.unscored > 0 && <p className="dl-warn">{t('preview.unscored', { count: preview.unscored })}</p>}
          {preview.sample.length > 0 && (
            <div className="dl-sample">
              <span className="dl-muted">{t('preview.sample')}</span>
              {preview.sample.slice(0, 12).map((s) => (
                <span key={s.fan_id} className="dl-chip dl-chip--static" title={s.fan_id}>
                  {s.name || (s.username ? `@${s.username}` : `#${s.fan_id}`)}
                </span>
              ))}
            </div>
          )}
        </>
      ) : (
        <LoadingBlock pad="24px 0" size={20} label={t('loading')} />
      )}
    </section>
  )
}

const LINKS_STALE_MS = 10 * 60_000
const LINKS_POLL_MS = 4_000
const LINKS_POLL_MAX = 15

/**
 * The Account's tracking links for the "Tracking link" filter (ADR 0032). The list is whatever the
 * worker last read from OnlyFans, so a never-read or old one asks for a fresh read and is re-fetched
 * until it lands (about a minute at most), instead of showing a stale list as if it were complete.
 */
function useTrackingLinks(api: DynamicListsApi, accountId: string) {
  const [state, setState] = useState<{ options: ListOption[] | null; error: string; syncing: boolean }>({ options: null, error: '', syncing: false })
  useEffect(() => {
    let live = true
    setState({ options: null, error: '', syncing: false })
    const wait = (ms: number) => new Promise((r) => setTimeout(r, ms))
    const run = async () => {
      let r = await api.trackingLinks(accountId)
      if (!live) return
      const stale = !r.syncedAt || Date.now() - Date.parse(r.syncedAt) > LINKS_STALE_MS
      setState({ options: r.links, error: '', syncing: stale })
      if (!stale) return
      const before = r.syncedAt
      await api.refreshTrackingLinks(accountId)
      for (let i = 0; i < LINKS_POLL_MAX && live; i++) {
        await wait(LINKS_POLL_MS)
        if (!live) return
        r = await api.trackingLinks(accountId)
        if (!live) return
        const done = r.syncedAt != null && r.syncedAt !== before
        setState({ options: r.links, error: '', syncing: !done })
        if (done) return
      }
      if (live) setState((s) => ({ ...s, syncing: false }))
    }
    run().catch((e: unknown) => {
      if (!live) return
      // A failed refresh keeps the links already shown; only a failed first read is an error.
      setState((s) => (s.options ? { ...s, syncing: false } : { options: null, error: e instanceof Error ? e.message : String(e), syncing: false }))
    })
    return () => {
      live = false
    }
  }, [api, accountId])
  return state
}

interface Props {
  api: DynamicListsApi
  accountId: string
  catalog: Catalog
  lists: ListOption[] | null
  listsMeta?: ListsMeta
  /** Absent for a new list. */
  list?: DynamicList
  /** Saves an edit elsewhere (the All view saves to every Account's copy). */
  saveEdit?: (patch: { name?: string; schedule: Schedule; rule: Rule }) => Promise<void>
  /** The name can no longer change (an OnlyFans list exists). */
  nameLocked?: boolean
  /** Shown above the fields. */
  note?: string
  onSaved: (list: DynamicList) => void
  onClose: () => void
}

export function ListEditor({ api, accountId, catalog, lists, listsMeta, list, saveEdit, nameLocked, note, onSaved, onClose }: Props) {
  const { t } = useTranslation('dynamicLists')
  const [name, setName] = useState(list?.name ?? '')
  const [schedule, setSchedule] = useState<Schedule>(list?.schedule ?? 'regular')
  const [rule, setRule] = useState<Rule>(list?.rule ?? newRule())
  const [templateKey, setTemplateKey] = useState<string | null>(list?.template_key ?? null)
  const [saving, setSaving] = useState<'off' | 'on' | null>(null)
  const [error, setError] = useState('')

  const available = useMemo(() => new Set(catalog.filters.filter((f) => f.available).map((f) => f.type as FilterType)), [catalog])
  const problem = ruleProblem(rule, available)
  const problemText = problem ? t(problem.key, problem.values) : ''
  const preview = usePreview(api, accountId, rule, !problem, list?.of_list_id)
  const nameFixed = nameLocked ?? Boolean(list?.of_list_id)
  const links = useTrackingLinks(api, accountId)

  const applyTemplate = (key: string) => {
    const tpl = catalog.templates.find((x) => x.key === key)
    if (!tpl) return
    setRule(tpl.rule)
    setSchedule(tpl.schedule)
    setTemplateKey(tpl.key)
    const tplName = (x: Template) => templateText(x, t).name
    if (!name.trim() || catalog.templates.some((x) => tplName(x) === name)) setName(tplName(tpl))
  }

  const save = async (enabled: boolean) => {
    if (!name.trim()) return setError(t('problem.name'))
    setSaving(enabled ? 'on' : 'off')
    setError('')
    try {
      if (list && saveEdit) {
        await saveEdit({ ...(nameFixed ? {} : { name }), schedule, rule })
        return onSaved(list)
      }
      const saved = list
        ? await api.update(list.id, { ...(nameFixed ? {} : { name }), schedule, rule })
        : await api.create(accountId, { name, schedule, rule, template_key: templateKey, enabled })
      onSaved(saved)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setSaving(null)
    }
  }

  const blocked = Boolean(problem) || Boolean(saving)
  return (
    <Modal
      side
      wide={!list}
      title={list ? t('editor.editTitle', { name: list.name }) : t('editor.newTitle')}
      onClose={onClose}
      footer={
        <>
          {error && <p className="dl-error dl-foot-error">{error}</p>}
          <button type="button" className="dl-btn dl-btn--ghost" onClick={onClose}>
            {t('cancel')}
          </button>
          {list ? (
            <button type="button" className="dl-btn dl-btn--primary" disabled={blocked} onClick={() => void save(list.enabled)}>
              <BusyLabel busy={saving} label={t('saving')}>{t('save')}</BusyLabel>
            </button>
          ) : (
            <>
              <button type="button" className="dl-btn" disabled={blocked} onClick={() => void save(false)}>
                <BusyLabel busy={saving === 'off'} label={t('saving')}>{t('editor.createOff')}</BusyLabel>
              </button>
              <button type="button" className="dl-btn dl-btn--primary" disabled={blocked} onClick={() => void save(true)}>
                <BusyLabel busy={saving === 'on'} label={t('saving')}>{t('editor.createOn')}</BusyLabel>
              </button>
            </>
          )}
        </>
      }
    >
      <div className={`dl-editor ${list ? '' : 'dl-editor--split'}`}>
        <div className="dl-editor-main">
          {note && <p className="dl-notice">{note}</p>}
          <label className="dl-field">
            <span className="dl-label dl-label--big">{t('editor.name')}</span>
            <input
              className="dl-input"
              value={name}
              maxLength={64}
              disabled={nameFixed}
              placeholder={t('editor.namePlaceholder')}
              onChange={(e) => setName(e.target.value)}
            />
            {nameFixed && <span className="dl-hint">{t('editor.nameFixed')}</span>}
          </label>
          <div className="dl-field">
            <span className="dl-label dl-label--big">{t('editor.schedule')}</span>
            <div className="dl-choice" role="group">
              {(['regular', 'one_time'] as Schedule[]).map((s) => (
                <button key={s} type="button" className={`dl-choice-btn ${schedule === s ? 'is-on' : ''}`} aria-pressed={schedule === s} onClick={() => setSchedule(s)}>
                  <FeatherIcon name={s === 'regular' ? 'refresh-cw' : 'camera'} size={14} /> {t(`card.schedule.${s}`)}
                </button>
              ))}
            </div>
            <span className="dl-hint">{t(`editor.scheduleHint.${schedule}`)}</span>
          </div>
          <RuleEditor
            rule={rule}
            onChange={(r) => {
              setRule(r)
              setTemplateKey(null)
            }}
            filters={catalog.filters}
            lists={lists}
            listsMeta={listsMeta}
            ownListId={list?.of_list_id}
            links={links.options}
            linksMeta={{ error: links.error || undefined, syncing: links.syncing }}
          />
          <PreviewPanel {...preview} problem={problemText} />
        </div>
        {!list && (
          <aside className="dl-editor-aside" aria-label={t('editor.templates')}>
            <h3 className="dl-h3">{t('editor.templates')}</h3>
            <span className="dl-hint">{t('editor.templatesHint')}</span>
            <div className="dl-templates">
              {catalog.templates.map((tpl) => {
                const text = templateText(tpl, t)
                return (
                  <button
                    key={tpl.key}
                    type="button"
                    className={`dl-template ${templateKey === tpl.key ? 'is-on' : ''}`}
                    aria-pressed={templateKey === tpl.key}
                    aria-label={text.name}
                    onClick={() => applyTemplate(tpl.key)}
                  >
                    <span className="dl-template-name">
                      {text.icon && <span aria-hidden="true">{text.icon} </span>}
                      {text.name}
                    </span>
                    <span className="dl-template-desc">{text.description}</span>
                  </button>
                )
              })}
            </div>
          </aside>
        )}
      </div>
    </Modal>
  )
}
